import React, { useState, useEffect, useRef, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { ThemeProvider } from '@material-ui/core/styles';
import { theme } from '../../../config/theme';
import { useParams } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import HourglassIcon from '@material-ui/icons/HourglassEmpty';
import clsx from 'clsx';
import axios from 'axios';
import CampaignSkeleton from '../../components/CampaignSkeleton';
import OrderDetailsSkeleton from './OrderDetailsSkeleton';
import QRCodeSkeleton from './QRCodeSkeleton';
import OrderDetails from './OrderDetails';
import ResponsiveContainer from '../../components/ResponsiveContainer';
import OrderQRCode from './OrderQRCode';
import OrderProductCard from './OrderProductCard';
import OrderTransactionMeta from './OrderTransactionMeta';
import OrderSuccessPanel from './OrderSuccessPanel';
import OrderRepurchaseSection from './OrderRepurchaseSection';
import OrderInstantPrizePanel from './OrderInstantPrizePanel';
import OrderGamePanel from './OrderGamePanel';
import OrderGamePanelSkeleton from './OrderGamePanelSkeleton';
import { UserContext } from '../../InnerApp';
import { trackEvent } from '../../utils';
import InstantWinnerToast from '../../components/InstantWinnerToast';

const STATUS_TEXT_SIZES = {
  title: '1.15rem',
  titleXs: '1.02rem',
  tag: '0.88rem',
};

const useStyles = makeStyles((theme) => ({
  outerBlue: {
    backgroundColor: '#E8E8E8',
    padding: '16px 12px 28px',
    width: '100%',
    boxSizing: 'border-box',
    minHeight: '65vh',
  },
  outerPaid: {
    backgroundColor: '#E8E8E8',
  },
  main: {
    textAlign: 'left',
  },
  statusHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    marginBottom: '16px',
    flexWrap: 'nowrap',
    minWidth: 0,
  },
  statusHeaderPaid: {
    marginBottom: '10px',
  },
  statusLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    minWidth: 0,
    flex: '1 1 auto',
    overflow: 'hidden',
  },
  statusIcon: {
    flexShrink: 0,
  },
  statusIconPaid: {
    fontSize: '2.25rem',
  },
  statusTitle: {
    fontWeight: 800,
    color: '#1A1A1A',
    fontSize: STATUS_TEXT_SIZES.title,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [theme.breakpoints.down('xs')]: {
      fontSize: STATUS_TEXT_SIZES.titleXs,
    },
  },
  statusTitlePaid: {
    color: '#2A5B2C',
  },
  statusTag: {
    fontSize: STATUS_TEXT_SIZES.tag,
    fontWeight: 600,
    color: '#3B7E3D',
    flexShrink: 0,
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  statusTagPaid: {
    fontWeight: 600,
    color: '#6FC472',
  },
}));

export default function OrderPage({ currentUser, influencer }) {
  const classes = useStyles();
  let { orderId, influencer_username } = useParams();
  const [loading, setLoading] = useState(true);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [fetchOrderDelay, setFetchOrderDelay] = useState(1000);
  const unpaidOrder = useRef(false);
  const purchaseTracked = useRef(false);
  const { setCurrentUser } = useContext(UserContext);

  const usernameForRoutes = influencer_username || influencer?.username;

  const isPaidSuccess =
    currentOrder && ['paid', 'completed'].includes(currentOrder.status);
  const hasInstantPrize = (currentOrder?.instant_prizes || []).length > 0;
  const gamificationEnabled = !!currentOrder?.gamification_enabled;
  // Bilhetes de incentivo ainda sendo gerados no backend: segura o poll e a
  // tag "Gerando seus bilhetes…". awaitingGames = subconjunto gamificado,
  // decide o skeleton de jogos (sub sem caixinha não mostra copy de jogos).
  const awaitingIncentive = currentOrder?.awaiting_incentive_tickets === true;
  const awaitingGames = currentOrder?.awaiting_incentive_games === true;
  // Did the order include any incentive subcampaign that actually had instant
  // prizes to compete for? Used to phrase the repurchase copy honestly.
  const competedForInstant = (currentOrder?.subcampaign_tickets || []).some(
    (s) => s.competed_for_instant
  );

  async function fetchCurrentUser() {
    const response = await axios.get('/api/v1/customers/current');
    setCurrentUser(response.data);
  }

  function fetchOrder() {
    axios
      .get(`/api/v1/purchase_orders/${orderId}.json`)
      .then((response) => {
        setCurrentOrder(response.data);
        if (response.data.status != 'completed') {
          unpaidOrder.current = true;
        }
        if (response.data.status == 'created') {
          setTimeout(() => {
            fetchOrder();
          }, fetchOrderDelay);
          setFetchOrderDelay(fetchOrderDelay * 2);
        } else if (response.data.status == 'waiting_payment') {
          setTimeout(() => {
            fetchOrder();
          }, 12000);
        } else if (response.data.status == 'paid') {
          if (!purchaseTracked.current) {
            const purchaseValue = parseFloat(response.data.total_value);
            trackEvent(
              'Purchase',
              {
                value: purchaseValue,
                currency: 'BRL',
                transaction_id: String(response.data.id),
                purchase_count: response.data.customer_purchase_count,
                ...(response.data.affiliate_link_id && { affiliate_id: String(response.data.affiliate_link_id) }),
                ...(response.data.affiliate_name && { affiliate_name: response.data.affiliate_name })
              },
              { identity: response.data.id }
            );
            purchaseTracked.current = true;
          }
          // Gerando bilhetes (pai e/ou incentivo): poll mais curto pra tela de
          // espera virar sozinha, sem refresh manual.
          const paidPollMs = response.data.awaiting_incentive_tickets === true ? 10000 : 20000;
          setTimeout(() => {
            fetchOrder();
          }, paidPollMs);
        } else if (response.data.status == 'completed') {
          const stillPending = (response.data.gamification_enabled === true) &&
            (response.data.instant_prizes || []).some((p) => p.game_status === 'pending');
          // awaiting_incentive_tickets: pedido completou mas os bilhetes de
          // incentivo ainda estão sendo gerados — sem re-buscar aqui a página
          // congela sem o painel de jogos.
          if (stillPending || response.data.awaiting_incentive_tickets === true) {
            setTimeout(() => {
              fetchOrder();
            }, 10000);
          }
          if (!purchaseTracked.current) {
            const purchaseValue = parseFloat(response.data.total_value);
            trackEvent(
              'Purchase',
              {
                value: purchaseValue,
                currency: 'BRL',
                transaction_id: String(response.data.id),
                purchase_count: response.data.customer_purchase_count,
                ...(response.data.affiliate_link_id && { affiliate_id: String(response.data.affiliate_link_id) }),
                ...(response.data.affiliate_name && { affiliate_name: response.data.affiliate_name })
              },
              { identity: response.data.id }
            );
            purchaseTracked.current = true;
          }
          fetchCurrentUser();
        }
      })
      .catch((error) => {
        setCurrentOrder(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    setLoading(true);
    purchaseTracked.current = false;
    fetchOrder();
  }, [orderId]);

  function statusIcon() {
    if (currentOrder == null) return null;
    if (['paid', 'completed'].includes(currentOrder.status))
      return (
        <CheckCircleIcon
          style={{ fill: '#56F84B' }}
          className={clsx(classes.statusIcon, classes.statusIconPaid)}
        />
      );
    if (['paid_after_time_limit', 'expired', 'error', 'canceled'].includes(currentOrder.status))
      return <ErrorIcon style={{ fill: 'red' }} className={classes.statusIcon} />;
    if (['created', 'waiting_payment'].includes(currentOrder.status))
      return <HourglassIcon style={{ fill: '#2D6E2F' }} className={classes.statusIcon} />;
  }

  return (
    <ThemeProvider theme={theme}>
      <div className={clsx(classes.outerBlue, isPaidSuccess && classes.outerPaid)}>
        <ResponsiveContainer>
          <InstantWinnerToast influencer={{ username: usernameForRoutes }} />
          <div className={classes.main}>
            <div
              className={clsx(classes.statusHeader, isPaidSuccess && classes.statusHeaderPaid)}
            >
              {loading ? (
                <Typography variant="h6" component="div" className={classes.statusTitle}>
                  Carregando...
                </Typography>
              ) : (
                <>
                  <div className={classes.statusLeft}>
                    {statusIcon()}
                    <span
                      className={clsx(classes.statusTitle, isPaidSuccess && classes.statusTitlePaid)}
                    >
                      {currentOrder?.user_status}
                    </span>
                  </div>
                  {(awaitingIncentive || currentOrder?.user_status_message) && (
                    <span
                      className={clsx(classes.statusTag, isPaidSuccess && classes.statusTagPaid)}
                    >
                      {awaitingIncentive
                        ? 'Gerando seus bilhetes…'
                        : currentOrder.user_status_message}
                    </span>
                  )}
                </>
              )}
            </div>
            {loading ? <CampaignSkeleton /> : <OrderProductCard order={currentOrder} />}
            {!loading && currentOrder && <OrderTransactionMeta order={currentOrder} />}
            {!loading && isPaidSuccess && (
              <>
                {gamificationEnabled ? (
                  <OrderGamePanel userId={currentUser?.id} />
                ) : awaitingGames ? (
                  <OrderGamePanelSkeleton />
                ) : (
                  hasInstantPrize && (
                    <OrderInstantPrizePanel
                      order={currentOrder}
                      influencerUsername={usernameForRoutes}
                    />
                  )
                )}
                <OrderSuccessPanel order={currentOrder} />
                {!gamificationEnabled && !awaitingIncentive && !hasInstantPrize && (
                  <OrderRepurchaseSection
                    order={currentOrder}
                    currentUser={currentUser}
                    influencerUsername={usernameForRoutes}
                    competedForInstant={competedForInstant}
                  />
                )}
              </>
            )}
            {loading ? (
              <QRCodeSkeleton />
            ) : (
              <OrderQRCode order={currentOrder} onPaidClick={fetchOrder} />
            )}
            {loading ? <OrderDetailsSkeleton /> : <OrderDetails order={currentOrder} />}
            {!loading && currentOrder == null && (
              <Typography variant="body1">
                Pedido inválido
                <br />
                <br />
                <br />
                <br />
              </Typography>
            )}
          </div>
        </ResponsiveContainer>
      </div>
    </ThemeProvider>
  );
}
